import { supabase } from '@/integrations/supabase/client';

/**
 * Cached Test-User Status
 * - null = not yet checked
 * - true = Test-User (use_test_stripe = true)
 * - false = normal user
 */
let cachedIsTestUser: boolean | null = null;

/**
 * Checks if current user is a test user
 */
async function checkIsTestUser(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    
    const { data } = await supabase
      .from('user_settings')
      .select('use_test_stripe')
      .eq('user_id', user.id)
      .single();
    
    return data?.use_test_stripe === true;
  } catch {
    return false;
  }
}

/**
 * Checks if logging is allowed (DEV or Test-User)
 */
function shouldLog(): boolean {
  if (import.meta.env.DEV) return true;
  return cachedIsTestUser === true;
}

/**
 * Privacy-focused logging utility
 * ONLY logs for:
 * - DEV mode (import.meta.env.DEV)
 * - Test users (use_test_stripe = true)
 * Never logs PII (names, emails, phone numbers, addresses)
 */
export const safeLog = {
  /**
   * Initialize test user status cache
   * Call once during app startup
   */
  init: async () => {
    cachedIsTestUser = await checkIsTestUser();
    if (shouldLog()) {
      console.log('[safeLog] Initialized:', { 
        isDev: import.meta.env.DEV, 
        isTestUser: cachedIsTestUser 
      });
    }
  },
  
  dev: (message: string, metadata?: Record<string, any>) => {
    if (shouldLog()) {
      console.log(`[DEV] ${message}`, metadata || '');
    }
  },
  
  info: (message: string, metadata?: Record<string, any>) => {
    if (shouldLog()) {
      console.log(`[INFO] ${message}`, metadata || '');
    }
  },
  
  error: (message: string, error?: Error) => {
    if (shouldLog()) {
      console.error(`[ERROR] ${message}`, error?.message || '');
    }
  },
  
  warn: (message: string, metadata?: Record<string, any>) => {
    if (shouldLog()) {
      console.warn(`[WARN] ${message}`, metadata || '');
    }
  },
  
  contact: (action: string, hasData: boolean) => {
    if (shouldLog()) {
      console.log(`[CONTACT] ${action}:`, { hasData, timestamp: Date.now() });
    }
  }
};
